/**
 * DocumentWorkspaceSharingModal Component
 * 
 * Modal for managing sharing settings of a Document Workspace.
 * Uses EntraUserPicker to search and select Entra users/groups.
 * Supports co-authoring - shared users can edit the document.
 */

import React, { useState, useEffect } from 'react';
import {
    Modal,
    PrimaryButton,
    DefaultButton,
    Stack,
    Toggle,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Label,
    Separator
} from '@fluentui/react';
import {
    PersonAddRegular,
    PeopleRegular,
    GlobeRegular,
    LockClosedRegular,
    DocumentRegular
} from '@fluentui/react-icons';
import { EntraUserPicker } from '../EntraUserPicker';
import { 
    updateWorkspaceSharing, 
    getWorkspaceSharing,
    DocumentWorkspace
} from '../../api/documentWorkspaceApi';

interface DocumentWorkspaceSharingModalProps {
    workspace: DocumentWorkspace;
    isOpen: boolean;
    onClose: () => void;
    onUpdated: (workspace: DocumentWorkspace) => void;
}

export const DocumentWorkspaceSharingModal: React.FC<DocumentWorkspaceSharingModalProps> = ({
    workspace,
    isOpen,
    onClose,
    onUpdated
}) => {
    const [sharedWithUsers, setSharedWithUsers] = useState<string[]>([]);
    const [isPublic, setIsPublic] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Load sharing settings when modal opens
    useEffect(() => {
        if (isOpen && workspace?.id) {
            loadSharingSettings();
        }
    }, [isOpen, workspace?.id]);

    // Reset messages when modal opens
    useEffect(() => {
        if (isOpen) {
            setSuccess(false);
            setError(null);
        }
    }, [isOpen]);

    const loadSharingSettings = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const sharing = await getWorkspaceSharing(workspace.id);
            setSharedWithUsers(sharing.shared_with_users || []);
            setIsPublic(sharing.is_public || false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load sharing settings');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSuccess(false);

        try {
            const updatedWorkspace = await updateWorkspaceSharing(workspace.id, {
                shared_with_users: sharedWithUsers,
                is_public: isPublic
            });
            
            setSuccess(true);
            onUpdated(updatedWorkspace);
            
            // Close after a brief delay to show success
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to update sharing settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        if (!isSaving) {
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={handleClose}
            isBlocking={isSaving}
            styles={{
                main: {
                    maxWidth: '500px',
                    minWidth: '400px'
                }
            }}
        >
            {/* Header */}
            <div style={{
                padding: '20px 24px 16px',
                borderBottom: '1px solid #e1e1e1',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <PersonAddRegular style={{ fontSize: '24px', color: '#0078d4' }} />
                <div>
                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                        Share Document Workspace
                    </h2>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <DocumentRegular style={{ fontSize: '14px' }} />
                        {workspace?.title || 'Untitled Document'}
                    </p>
                </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px' }}>
                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spinner size={SpinnerSize.large} label="Loading sharing settings..." />
                    </div>
                ) : (
                    <>
                        {error && (
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                onDismiss={() => setError(null)}
                                styles={{ root: { marginBottom: '16px' } }}
                            >
                                {error}
                            </MessageBar>
                        )}

                        {success && (
                            <MessageBar
                                messageBarType={MessageBarType.success}
                                styles={{ root: { marginBottom: '16px' } }}
                            >
                                Sharing settings updated successfully!
                            </MessageBar>
                        )}

                        <Stack tokens={{ childrenGap: 20 }}>
                            {/* Public toggle */}
                            <div style={{
                                padding: '16px',
                                backgroundColor: isPublic ? '#e3f2fd' : '#fafafa',
                                borderRadius: '8px',
                                border: isPublic ? '1px solid #0078d4' : '1px solid #e1e1e1',
                                transition: 'all 0.2s ease'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                    <div style={{ 
                                        marginTop: '2px',
                                        color: isPublic ? '#0078d4' : '#666'
                                    }}>
                                        {isPublic ? (
                                            <GlobeRegular style={{ fontSize: '20px' }} />
                                        ) : (
                                            <LockClosedRegular style={{ fontSize: '20px' }} />
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Toggle
                                            label="Make this workspace public"
                                            checked={isPublic}
                                            onChange={(_, checked) => setIsPublic(!!checked)}
                                            disabled={isSaving}
                                            styles={{ root: { marginBottom: '4px' } }}
                                        />
                                        <p style={{ 
                                            margin: 0, 
                                            fontSize: '12px', 
                                            color: '#666',
                                            lineHeight: 1.4
                                        }}>
                                            {isPublic 
                                                ? 'Anyone in your organization can view this document (read-only).'
                                                : 'Only you and people you share with can access this document.'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <Separator />

                            {/* User picker */}
                            <div>
                                <Label style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '6px',
                                    marginBottom: '8px'
                                }}>
                                    <PeopleRegular style={{ fontSize: '16px' }} />
                                    Share with collaborators
                                </Label>
                                
                                <EntraUserPicker
                                    label=""
                                    placeholder="Search for users by name or email..."
                                    selectedEmails={sharedWithUsers}
                                    onSelectionChange={setSharedWithUsers}
                                    disabled={isSaving}
                                    maxSelection={50}
                                    description="Selected users can view AND edit this document."
                                />
                            </div>

                            {/* Co-authoring info box */}
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#e8f4fd',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#0078d4',
                                lineHeight: 1.5,
                                border: '1px solid #b3d7f2'
                            }}>
                                <strong>Co-authoring enabled:</strong> Shared users can edit sections of this document 
                                in real-time. You'll see who's editing which section, and changes are saved automatically.
                            </div>

                            {/* Ownership note */}
                            <div style={{
                                padding: '12px 16px',
                                backgroundColor: '#fff3cd',
                                borderRadius: '6px',
                                fontSize: '12px',
                                color: '#856404',
                                lineHeight: 1.5
                            }}>
                                <strong>Note:</strong> Only you (the owner) can export the document, delete it, 
                                or manage sharing settings.
                            </div>
                        </Stack>
                    </>
                )}
            </div>

            {/* Footer */}
            <div style={{
                padding: '16px 24px',
                borderTop: '1px solid #e1e1e1',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px'
            }}>
                <DefaultButton
                    text="Cancel"
                    onClick={handleClose}
                    disabled={isSaving || isLoading}
                />
                <PrimaryButton
                    text={isSaving ? 'Saving...' : 'Save Changes'}
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                >
                    {isSaving && <Spinner size={SpinnerSize.xSmall} style={{ marginRight: '8px' }} />}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                </PrimaryButton>
            </div>
        </Modal>
    );
};

export default DocumentWorkspaceSharingModal;
